import { type ChangeEvent, useEffect, useMemo } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Input, Select, SlimButton, Typography, UploadButton } from 'components'
import { OVTypes } from 'constants/enums'
import { Controller, useForm } from 'react-hook-form'
import { getReasonsJustification } from 'store/OVContext/ovSlice'
import { useDispatch, useSelector } from 'store/store'
import * as yup from 'yup'

import * as S from './styles'

const schema = yup.object().shape({
  codReason: yup.string().required(),
  descReason: yup.string().required(),
  descRequest: yup.string().required(),
  file: yup.mixed().optional(),
  file_name: yup.string().optional(),
})

export type JustifyNDFields = {
  codReason: string
  descReason: string
  descRequest: string
  file_name: string
  file: File
}

type JustifyNDFormProps = {
  setOpen: (open: boolean) => void
  sendAction: (data?: JustifyNDFields) => void
}

const JustifyNDForm = ({ setOpen, sendAction }: JustifyNDFormProps) => {
  const dispatch = useDispatch()
  const {
    ov: { reasonsJustification },
    salesOrder: { draftBody },
  } = useSelector((state) => state)

  const defaultValues = {
    codReason: '',
    descReason: '',
    descRequest: '',
    file: new File([], ''),
    file_name: '',
  }
  const {
    control,
    setValue,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const reasonOptions = useMemo(() => {
    return reasonsJustification
      .filter((item) => {
        // TODO: Use isYRBA from GLOBAL STATE
        if (draftBody.cd_tipo_orde !== OVTypes.YRBA) {
          return item.CodigoMotivoNegociacao !== '15' && item.CodigoMotivoNegociacao !== '12'
        }
        return true
      })
      .map((reason) => ({
        title: reason.DescricaoMotivoNegociacao,
        value: reason.CodigoMotivoNegociacao,
      }))
  }, [reasonsJustification])

  useEffect(() => {
    // TODO: Use isYRBA from GLOBAL STATE
    if (draftBody.cd_tipo_orde === OVTypes.YRBA) {
      const yrbaCode = '12' // TODO: Create an ENUM for this
      const yrbaReason = reasonOptions.find((item) => item.value === yrbaCode)
      setValue('codReason', yrbaCode)
      setValue('descReason', yrbaReason?.title as string)
    }
  }, [draftBody.cd_tipo_orde, reasonOptions])

  useEffect(() => {
    dispatch(getReasonsJustification())
  }, [])

  const handleSendSubmit = (data: JustifyNDFields) => {
    sendAction(data)
  }

  return (
    <S.Form onSubmit={handleSubmit(handleSendSubmit)}>
      <S.FieldsContainer>
        <Typography design="hav-default-03-text">
          A seleção para envio contém uma ou mais OVs identificadas como Negociação diferenciada
          (ND). É necessário justificá-las antes do envio.
        </Typography>

        <div>
          <Controller
            control={control}
            name={'codReason'}
            render={({ field: { name, onChange, value } }) => (
              <Select
                disabled={
                  !reasonOptions.length || draftBody.cd_tipo_orde === OVTypes.YRBA // TODO: Use isYRBA from STATE
                }
                label="Motivo da solicitação *"
                menuText={!reasonOptions.length ? 'Carregando opções...' : 'Selecione o motivo'}
                name={name}
                onChange={(e) => {
                  const reason = reasonOptions.find((r) => r.value === e.target.value)
                  reason && setValue('descReason', reason?.title)
                  onChange(e.target.value)
                }}
                options={reasonOptions}
                optionsMaxHeight={300}
                value={value}
              />
            )}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="descRequest"
            render={({ field: { onChange, value } }) => (
              <Input
                height={90}
                InputProps={{
                  inputProps: { maxLength: 264 },
                  placeholder: 'Digite aqui mais informações sobre a solicitação',
                }}
                label="Descrição da solicitação"
                onChange={onChange}
                required
                rows={3}
                value={value}
              />
            )}
          />
        </div>
        <S.UploadContainer>
          <Controller
            control={control}
            name="file"
            render={({ field: { onChange, value } }) => (
              <UploadButton
                id="fileInput"
                key={`fileInput`}
                name="upload"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  if (e.target.files?.length) {
                    onChange(e.target.files[0])
                    setValue(`file_name`, e.target.files[0].name, {
                      shouldDirty: true,
                    })
                  }
                }}
                onClear={() => {
                  setValue('file', new File([], ''))
                  setValue('file_name', '')
                }}
                placeholder="Clique aqui para selecionar arquivo"
                value={value as File}
              />
            )}
          />
        </S.UploadContainer>
      </S.FieldsContainer>

      <S.Footer>
        <SlimButton
          content="Voltar"
          onClick={() => setOpen(false)}
          type="button"
          variant="secondary"
        />
        <SlimButton content="Enviar" disabled={!isValid} type="submit" variant="primary" />
      </S.Footer>
    </S.Form>
  )
}

export default JustifyNDForm
